import {
  DEFAULT_TIME_ZONE,
  ERROR_CODE,
  type FacilityKey,
  Retry,
  SocketCommunicationPacketSchema,
  type SocketCommunicationPayload,
  UniversalError,
  parseSocketCommunicationPacket,
  serialize,
} from "@packages/core";
import { fetchAuthSession } from "aws-amplify/auth";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";
import { create } from "zustand";

import { ENVIRONMENT_VARIABLE } from "#environment-variable";

import { updateApplicationConfigurationQueryData } from "./application-configuration";
import { updateBedQueryData } from "./bed";
import { updateFacilityQueryData } from "./facility";
import { updateLatestFallRiskEntryQueryCache } from "./fall-risk-entry";
import { updateResidentQueryData } from "./resident";
import { updateSensorQueryData } from "./sensor";
import { updateLatestStatusQueryCache } from "./status";

let counter = 0;
const callbacks = new Map<
  number,
  {
    reject(reason: unknown): void;
    resolve(payload: SocketCommunicationPayload): void;
  }
>();

function sendPacket<T extends true | false>(
  socket: WebSocket | false | null | undefined,
  payload: SocketCommunicationPayload<false>,
  callback = false as T,
): T extends true ? Promise<SocketCommunicationPayload> : void {
  const request = callback ? counter++ : null;
  const message = JSON.stringify(serialize(parseSocketCommunicationPacket({ payload, request })));
  console.debug("ソケット通信でメッセージを送信します。", message);

  let promise;
  if (request !== null) {
    promise = new Promise<SocketCommunicationPayload>(function (resolve, reject) {
      callbacks.set(request, { reject, resolve });
    });
  }

  if (socket) {
    socket.send(message);
  } else if (request !== null) {
    callbacks.get(request)?.reject(new UniversalError(ERROR_CODE.SOCKET_COMMUNICATION_ERROR));
    callbacks.delete(request);
  }

  return promise as any;
}

interface SocketCommunicationManager {
  connect(force?: boolean): void;
  initialized: boolean;
  socket?: WebSocket | null;
  connectedAt: Temporal.PlainDateTime | null;
}

export const useSocketCommunicationManager = create<SocketCommunicationManager>(
  function (set, get) {
    const PREFIX = "[ソケット通信] ";

    function handleApplicationConfigurationDataPayload(
      payload: Extract<SocketCommunicationPayload, { type: "ApplicationConfigurationData" }>,
    ) {
      console.debug(PREFIX + "アプリケーション構成データペイロードを処理します。", payload);
      const { applicationConfiguration } = payload;
      updateApplicationConfigurationQueryData(applicationConfiguration);
    }

    function handleBedDataPayload(
      payload: Extract<SocketCommunicationPayload, { type: "BedData" }>,
    ) {
      console.debug(PREFIX + "ベッドデータペイロードを処理します。", payload);
      const { bed, bedKey } = payload;
      updateBedQueryData(bedKey, bed);
    }

    function handleFacilityDataPayload(
      payload: Extract<SocketCommunicationPayload, { type: "FacilityData" }>,
    ) {
      console.debug(PREFIX + "施設データペイロードを処理します。", payload);
      const { facility, facilityKey } = payload;
      updateFacilityQueryData(facilityKey, facility);
    }

    function handleFallRiskEntryDataPayload(
      payload: Extract<SocketCommunicationPayload, { type: "FallRiskEntryData" }>,
    ) {
      console.debug(PREFIX + "転倒リスクエントリデータペイロードを処理します。", payload);
      const { fallRiskEntry } = payload;
      const { userKey } = fallRiskEntry;
      updateLatestFallRiskEntryQueryCache(userKey, fallRiskEntry);
    }

    function handleResidentDataPayload(
      payload: Extract<SocketCommunicationPayload, { type: "ResidentData" }>,
    ) {
      console.debug(PREFIX + "入居者データペイロードを処理します。", payload);
      const { resident, userKey } = payload;
      updateResidentQueryData(userKey, resident);
    }

    function handleSensorDataPayload(
      payload: Extract<SocketCommunicationPayload, { type: "SensorData" }>,
    ) {
      console.debug(PREFIX + "センサーデータペイロードを処理します。", payload);
      const { sensor, sensorKey } = payload;
      updateSensorQueryData(sensorKey, sensor);
    }

    function handleStatusDataPayload(
      payload: Extract<SocketCommunicationPayload, { type: "StatusData" }>,
    ) {
      console.debug(PREFIX + "状態データデータペイロードを処理します。", payload);
      const { status } = payload;
      const { sensorKey, userKey } = status;
      updateLatestStatusQueryCache(sensorKey, status);
      updateLatestStatusQueryCache(userKey, status);
    }

    function onmessage(event: MessageEvent) {
      console.debug(PREFIX + "メッセージを受信しました。", event);

      let packet;
      try {
        packet = v.parse(SocketCommunicationPacketSchema(), JSON.parse(event.data));
      } catch (error) {
        console.error(PREFIX + "イベントデータが予期しない形式です。", error);
        return;
      }

      if (packet.response !== null) {
        const callback = callbacks.get(packet.response);

        if (callback) {
          if (packet.payload.type === "Error") {
            callback.reject(
              new UniversalError(packet.payload.code ?? ERROR_CODE.NATIVE_APPLICATION_ERROR),
            );
          } else {
            callback.resolve(packet.payload);
          }
        } else {
          console.warn(
            PREFIX +
              "宛先が" +
              packet.response +
              "であるパケットを受信しましたが、宛先が見つかりません。",
            packet,
          );
        }

        callbacks.delete(packet.response);
      }

      const { payload } = packet;
      switch (payload.type) {
        case "ApplicationConfigurationData":
          handleApplicationConfigurationDataPayload(payload);
          break;
        case "BedData":
          handleBedDataPayload(payload);
          break;
        case "FacilityData":
          handleFacilityDataPayload(payload);
          break;
        case "FallRiskEntryData":
          handleFallRiskEntryDataPayload(payload);
          break;
        case "ResidentData":
          handleResidentDataPayload(payload);
          break;
        case "SensorData":
          handleSensorDataPayload(payload);
          break;
        case "StatusData":
          handleStatusDataPayload(payload);
          break;
        default:
          console.debug(PREFIX + "このタイプのペイロードは処理されません。", payload);
          break;
      }
    }

    function connect(force?: boolean) {
      if (force || get().socket == null) {
        const socket = new WebSocket(ENVIRONMENT_VARIABLE.CHANNEL_ENTRY_POINT);
        socket.onclose = function (event: CloseEvent) {
          console.debug(PREFIX + "ソケット通信が終了しました。", event);
          set(function ({ socket }) {
            return socket === event.target ? { socket: null } : {};
          });
          connect();
        };
        socket.onerror = function (event: Event) {
          console.debug(PREFIX + "ソケット通信中にエラーが発生しました。", event);
          set(function ({ socket }) {
            return socket === event.target ? { socket: null } : {};
          });
          connect();
        };
        socket.onmessage = onmessage;
        socket.onopen = function (event: Event) {
          console.debug(PREFIX + "ソケット通信が開始されました。", event);
          const { socket: oldSocket } = get();

          const newSocket = event.target;
          if (!(newSocket instanceof WebSocket)) {
            console.error(PREFIX + "イベントターゲットが`WebSocket`ではありません。");
            return;
          }

          // 新しい接続が確立されたので、古い接続を終了する。
          if (oldSocket != null && newSocket !== oldSocket) {
            console.debug(PREFIX + "新しい接続が確立されたので、古い接続を終了します。");
            oldSocket.close();
          }

          set({
            connectedAt: Temporal.Now.plainDateTimeISO(DEFAULT_TIME_ZONE),
            initialized: true,
            socket: newSocket,
          });
        };
      }
    }

    const interval = /** 9分15秒 */ 555_000;
    setInterval(async function () {
      const { connectedAt, socket } = get();
      if (socket) {
        const now = Temporal.Now.plainDateTimeISO(DEFAULT_TIME_ZONE);
        if (
          connectedAt &&
          Temporal.PlainDateTime.compare(
            now.add({ seconds: 15, milliseconds: interval }),
            connectedAt.add({ hours: 1, minutes: 57 }),
          ) > 0
        ) {
          console.debug(PREFIX + "接続が確立されてから一定時間経過したため、再接続します。");
          connect(true);
        } else {
          try {
            console.debug(PREFIX + "Pingを送信します。");
            const { fromClientToServer, fromServerToClient } = await ping(socket);
            console.debug(
              PREFIX +
                "Pongを受信しました。往復に" +
                (fromClientToServer + fromServerToClient) +
                "㎳(往路" +
                fromClientToServer +
                "㎳／復路" +
                fromServerToClient +
                "㎳)要しました。",
            );
          } catch (error) {
            console.error(PREFIX + "Pingに失敗しました。", error);
          }
        }
      } else {
        console.debug(PREFIX + "ソケット通信が確立されていないので、接続を試みます。");
        connect();
      }
    }, interval);

    return { connect, connectedAt: null, initialized: false };
  },
);

export function useSocket() {
  return useSocketCommunicationManager(function ({ socket }) {
    return socket;
  });
}

export async function subscribeData(
  socket: WebSocket | false | null | undefined,
  parameters: { facilityKey: FacilityKey | null },
) {
  // 起動直後等、タイミング次第で失敗する事があるので、再試行する。
  const authSession = await Retry(
    5,
    1000,
    1000,
  )(async function () {
    return fetchAuthSession();
  });

  await sendPacket(
    socket,
    {
      type: "SubscribeDataRequest",
      ...parameters,
      accessToken: authSession.tokens ? authSession.tokens.accessToken.toString() : null,
      subscribedAt: Temporal.Now.plainDateTimeISO(DEFAULT_TIME_ZONE),
    },
    true,
  );
}

async function ping(socket: WebSocket | false | null | undefined) {
  const now1 = Temporal.Now.plainDateTimeISO(DEFAULT_TIME_ZONE);
  const response = await Promise.race([
    sendPacket(socket, { type: "Ping", now: now1 }, true),
    new Promise<never>(function (_, reject) {
      setTimeout(function () {
        reject(
          new UniversalError(
            ERROR_CODE.SOCKET_COMMUNICATION_ERROR,
            "Pongが10秒以内に返されませんでした。",
          ),
        );
      }, 10_000);
    }),
  ]);
  if (response.type !== "Pong") {
    throw new UniversalError(
      ERROR_CODE.SOCKET_COMMUNICATION_ERROR,
      "PingにPong以外が返されました。",
    );
  }
  const { now: now2 } = response;
  const now3 = Temporal.Now.plainDateTimeISO(DEFAULT_TIME_ZONE);

  return {
    fromClientToServer: now2.since(now1).total("milliseconds"),
    fromServerToClient: now3.since(now2).total("milliseconds"),
  };
}

// 再表示された時に念の為接続を試みる。
document.addEventListener("visibilitychange", function () {
  if (document.visibilityState === "visible") {
    useSocketCommunicationManager.getState().connect();
  }
});

// 常時接続を開始する。
useSocketCommunicationManager.getState().connect();
